import {
  Box,
  Container,
  Tabs,
  Tab,
  Paper,
  Stack,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Popover,
  Card,
  CardContent,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CreateButton from "../common/createButton";
import SearchBar from "../common/searchBar";
import { useCurrentProfile } from "../../ContextWrapper";
import { get } from "../../api/api";

export default function LayoutShell({
  title,
  tabs,
  activeTab,
  onTabChange,
  children,
  cogMenuItems = [],
  onCreateClick,
  onSearchChange,
  searchValue,
  searchContext = "default",
  createButtonText = "Create",
  showBackButton = false,
  onBackClick,
  showCreateButton = true,
}) {
  const navigate = useNavigate();
  const { profile, fireToast } = useCurrentProfile();
  const [userData, setUserData] = useState(null);

  // Fetch user data when profile is available
  useEffect(() => {
    if (profile?.id) {
      get(`/api/v2/profiles/${profile.id}/`)
        .then((fullProfile) => {
          setUserData(fullProfile.user);
        })
        .catch((error) => {
          console.error("Error fetching user data:", error);
        });
    }
  }, [profile?.id]);

  // State for notification popover
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const notificationOpen = Boolean(notificationAnchorEl);

  const [cogMenuAnchorEl, setCogMenuAnchorEl] = useState(null);
  const cogMenuOpen = Boolean(cogMenuAnchorEl);

  // State for profile menu
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
  const profileMenuOpen = Boolean(profileMenuAnchorEl);

  // Notification bell handlers
  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleCogMenuClick = (event) => {
    setCogMenuAnchorEl(event.currentTarget);
  };

  const handleCogMenuClose = () => {
    setCogMenuAnchorEl(null);
  };

  const handleCogMenuNavigate = (path) => {
    handleCogMenuClose();
    navigate(path);
  };

  // Profile menu handlers
  const handleProfileMenuClick = (event) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleProfileSettings = () => {
    handleProfileMenuClose();
    fireToast(
      "Profile Settings",
      "Under construction, coming soon....",
      "info"
    );
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    navigate("/logout");
  };

  const getDisplayName = () => {
    // Try to get user data from the fetched userData
    if (userData?.first_name || userData?.last_name) {
      const fullName = `${userData.first_name || ""} ${
        userData.last_name || ""
      }`.trim();
      return fullName;
    }

    // Fallback: Extract name from email (which is mapped from username)
    if (userData?.email) {
      const email = userData.email;
      const nameFromEmail = email.split("@")[0];
      const formattedName =
        nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1);
      return formattedName;
    }

    // Final fallback
    const fallback = "User";
    return fallback;
  };

  const getUserTypeDisplay = () => {
    const type = profile?.type;
    switch (type) {
      case "admin":
        return "Lucro Admin";
      case "accountant":
        return "Lucro Accountant";
      case "client":
        return "Lucro Customer";
      default:
        return "Lucro User";
    }
  };

  const getProfilePictureUrl = () => {
    return null;
  };

  const getUserInitials = () => {
    const firstName = userData?.first_name || "";
    const lastName = userData?.last_name || "";
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) return firstName.charAt(0).toUpperCase();
    if (userData?.email) return userData.email.charAt(0).toUpperCase();
    return "U";
  };
  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh", pt: 0 }}>
      <Container maxWidth="100%">
        <Stack spacing={2} sx={{ width: "98%", mx: "auto", pt: 3 }}>
          {/* Header with title and actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pb: 2,
              position: "relative",
            }}
          >
            {showBackButton && (
              <IconButton
                onClick={onBackClick}
                sx={{
                  ml: -3,
                  mr: 1,
                  color: "#374151",
                  "&:hover": {
                    backgroundColor: "rgba(55, 65, 81, 0.04)",
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <SearchBar
                key={`search-${searchContext}`}
                onSearchChange={onSearchChange}
                value={searchValue}
                placeholder="Search here..."
                contextLabel={searchContext}
              />
              {showCreateButton && (
                <CreateButton onClick={onCreateClick}>
                  {createButtonText}
                </CreateButton>
              )}

              {/* 1. Notification Bell */}
              <IconButton
                onClick={handleNotificationClick}
                sx={{
                  color: "white",
                  border: "2px solid #d1d5db",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  backgroundColor: "white",
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    borderColor: "#9ca3af",
                  },
                }}
              >
                <NotificationsIcon
                  sx={{ color: "#6b7280", fontSize: "1.125rem" }}
                />
              </IconButton>

              {cogMenuItems.length > 0 && (
                <IconButton
                  onClick={handleCogMenuClick}
                  sx={{
                    color: "white",
                    border: "2px solid #d1d5db",
                    borderRadius: "50%",
                    width: 36,
                    height: 36,
                    backgroundColor: "white",
                    "&:hover": {
                      backgroundColor: "#f9fafb",
                      borderColor: "#9ca3af",
                    },
                  }}
                >
                  <SettingsIcon
                    sx={{ color: "#6b7280", fontSize: "1.125rem" }}
                  />
                </IconButton>
              )}

              {/* 2. Profile Picture and Info */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar
                  src={getProfilePictureUrl()}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "#627CFB",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                  }}
                >
                  {getUserInitials()}
                </Avatar>

                <Box
                  sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#1f2937",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {getDisplayName()}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#6b7280",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getUserTypeDisplay()}
                  </Typography>
                </Box>

                {/* 3. Dropdown Arrow */}
                <IconButton
                  onClick={handleProfileMenuClick}
                  sx={{
                    color: "#6b7280",
                    padding: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(107, 114, 128, 0.1)",
                    },
                  }}
                >
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Stack>
      </Container>

      {/* Horizontal line above tab */}
      <Box
        sx={{
          width: "100%",
          borderTop: "2px solid rgba(229, 231, 235, 0.5)",
        }}
      />

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          overflow: "hidden",
          boxShadow: "none",
          width: "100%",
        }}
      >
        <Box sx={{ borderBottom: "1px solid #e5e7eb" }}>
          <Tabs
            value={activeTab}
            onChange={onTabChange}
            sx={{
              px: 3,
              "& .MuiTabs-indicator": {
                backgroundColor: "#627CFB",
                height: 2,
              },
              "& .MuiTabs-flexContainer": {
                gap: 2,
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  fontSize: "0.875rem",
                  color: "#6b7280",
                  "&.Mui-selected": {
                    color: "#627CFB",
                    fontWeight: 600,
                  },
                  "&:hover": {
                    color: "#374151",
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>
      </Paper>

      <Container maxWidth="100%" sx={{ mt: 3 }}>
        <Stack spacing={1} sx={{ width: "99.5%", mx: "auto" }}>
          {/* Main content */}
          <Box sx={{ p: 3, backgroundColor: "white", borderRadius: "12px" }}>
            {children}
          </Box>
        </Stack>
      </Container>

      {/* Notification Popover */}
      <Popover
        open={notificationOpen}
        anchorEl={notificationAnchorEl}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          mt: 1,
          zIndex: 9999,
        }}
        slotProps={{
          paper: {
            sx: {
              zIndex: 9999,
            },
          },
        }}
      >
        <Card sx={{ minWidth: 250, maxWidth: 300 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No new notifications
            </Typography>
          </CardContent>
        </Card>
      </Popover>

      <Menu
        anchorEl={cogMenuAnchorEl}
        open={cogMenuOpen}
        onClose={handleCogMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          mt: 1,
          zIndex: 9999,
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            backgroundColor: "#627CFB",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            zIndex: 9999,
          },
        }}
      >
        {cogMenuItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => handleCogMenuNavigate(item.path)}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: "#536df1",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#ffffff",
                fontWeight: 500,
                textTransform: "none",
              }}
            >
              {item.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileMenuAnchorEl}
        open={profileMenuOpen}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          mt: 1,
          zIndex: 9999,
        }}
        PaperProps={{
          sx: {
            minWidth: 160,
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            zIndex: 9999,
          },
        }}
      >
        <MenuItem onClick={handleProfileSettings} sx={{ py: 1.5, px: 2 }}>
          <Typography variant="body2">Profile Settings</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ py: 1.5, px: 2 }}>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}
